import AsyncStorage from '@react-native-async-storage/async-storage';
import { usePreventScreenCapture } from 'expo-screen-capture';
import { Stack, useRootNavigationState, useRouter, useSegments } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

import { AnnouncementModal } from '@/components/AnnouncementModal';
import { ConsentBanner } from '@/components/ConsentBanner';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { WebWatermarkOverlay } from '@/components/WebWatermark';
import { useUpdateCheck, type UpdateStatus } from '@/hooks/useUpdateCheck';
import { useOtaUpdate } from '@/hooks/useOtaUpdate';
import { FreezeBanner } from '@/components/FreezeBanner';
import { IncomingMessageToast } from '@/components/IncomingMessageToast';
import { Level2ReviewModal, LEVEL2_REVIEW_KEY } from '@/components/Level2ReviewModal';
import { LevelUpOverlay } from '@/components/LevelUpOverlay';
import { ProfessionReminderModal } from '@/components/ProfessionReminderModal';
import { TabBar } from '@/components/TabBar';
import { Tutorial, subscribeTutorialActive } from '@/components/Tutorial';
import { useIsWideWeb, WebShell } from '@/components/WebShell';
import { TabTransitionProvider } from '@/contexts/TabTransitionContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAnnouncement } from '@/hooks/useAnnouncement';
import { useAutoClearCacheOnUpdate } from '@/hooks/useAutoClearCacheOnUpdate';
// usePresenceHeartbeat: προσωρινά απενεργοποιημένο — ύποπτο για crash στο
// άνοιγμα της εφαρμογής (μοναδικό νέο κομμάτι που έτρεχε αδιακρίτως σε ΚΑΘΕ
// cold start). Θα ξαναενεργοποιηθεί μετά από πιο προσεκτικό testing.
// import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';
import { type LevelInfo } from '@/components/ui/LevelBadge';
import { getUserLevel } from '@/lib/levels';
import { isProfileComplete } from '@/lib/profiles';
import { initSounds } from '@/lib/sounds';
import { supabase } from '@/lib/supabase';

function useRequireCompleteProfile() {
  const { profile, loading } = useProfile();
  const segments = useSegments();
  const router = useRouter();
  const navState = useRootNavigationState();

  useEffect(() => {
    if (!navState?.key) return;
    if (loading) return;

    const onOnboarding = segments[segments.length - 1] === 'onboarding';
    const complete = isProfileComplete(profile);

    if (!complete && !onOnboarding) {
      router.replace('/(app)/onboarding');
    } else if (complete && onOnboarding) {
      router.replace('/(app)');
    }
  }, [profile, loading, segments, router, navState?.key]);
}

function useLevelUpCheck() {
  const { user } = useAuth();
  const [pendingLevelUp, setPendingLevelUp] = useState<LevelInfo | null>(null);

  const check = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('level_up_events')
      .select('id, new_level')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!data) return;

    const info = await getUserLevel().catch(() => null);
    if (info) setPendingLevelUp(info);

    await supabase.from('level_up_events').delete().eq('id', data.id);
  }, [user]);

  return { pendingLevelUp, clearLevelUp: () => setPendingLevelUp(null), check };
}

export default function AppLayout() {
  const { profile, loading } = useProfile();
  const { colors } = useTheme();
  const isWideWeb = useIsWideWeb();
  useRequireCompleteProfile();
  usePushNotifications();
  useAutoClearCacheOnUpdate();
  // usePresenceHeartbeat(); — βλ. σχόλιο στο import
  // expo-screen-capture δεν υποστηρίζεται στο web (@platform android/ios μόνο)
  // — αν κληθεί εκεί πετάει uncaught promise rejection σε κάθε mount.
  if (Platform.OS !== 'web') {
    usePreventScreenCapture();
  }
  const { pendingLevelUp, clearLevelUp, check } = useLevelUpCheck();
  const { announcement, visible: showAnnouncement, dismiss: dismissAnnouncement } = useAnnouncement();
  const [showLevel2Review, setShowLevel2Review] = useState(false);
  const [showProfessionReminder, setShowProfessionReminder] = useState(false);
  const professionCheckedRef = useRef(false);
  const updateStatus = useUpdateCheck();
  const { ready: otaReady, changelog: otaChangelog, applyUpdate: applyOtaUpdate } = useOtaUpdate();
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  useEffect(() => subscribeTutorialActive(setTutorialActive), []);
  // Το native store update (forced/optional) έχει προτεραιότητα έναντι του OTA,
  // αφού μπορεί να περιλαμβάνει αλλαγές που το OTA δεν καλύπτει.
  const effectiveUpdateStatus: UpdateStatus =
    updateStatus.type !== 'none' ? updateStatus : otaReady ? { type: 'ota', startUpdate: applyOtaUpdate, changelog: otaChangelog } : { type: 'none' };

  useEffect(() => { void initSounds(); }, []);
  useEffect(() => { void check(); }, [check]);

  // Μία φορά ανά άνοιγμα εφαρμογής — αν ο επαγγελματικός λογαριασμός είναι
  // ενεργός αλλά δεν έχει δηλωθεί επάγγελμα, υπενθύμισε το.
  useEffect(() => {
    if (loading || professionCheckedRef.current) return;
    professionCheckedRef.current = true;
    if (profile?.portfolio_enabled && !profile?.profession_category_id) {
      setShowProfessionReminder(true);
    }
  }, [loading, profile]);

  async function handleLevelUpDone() {
    const level = pendingLevelUp?.level;
    clearLevelUp();
    if (level === 2) {
      const seen = await AsyncStorage.getItem(LEVEL2_REVIEW_KEY);
      if (!seen) setShowLevel2Review(true);
    }
  }

  if (loading && !profile) {
    return (
      <View style={[styles.loader, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <TabTransitionProvider>
    <View style={[styles.flex, { backgroundColor: colors.background }]}>
      <WebShell>
      <Stack
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: colors.background },
          animation: 'default',
          gestureEnabled: true,
          gestureDirection: 'horizontal',
          animationTypeForReplace: 'push',
          fullScreenGestureEnabled: true,
        }}
      >
        {/* Tab screens: instant switch, no slide */}
        <Stack.Screen name="index"            options={{ animation: 'none', gestureEnabled: false }} />
        <Stack.Screen name="agreements/index" options={{ animation: 'none', gestureEnabled: false }} />
        <Stack.Screen name="profile"          options={{ animation: 'none', gestureEnabled: false }} />
        {/* Modal screens: slide up */}
        <Stack.Screen name="agreements/[id]"   options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="agreements/new"   options={{ animation: 'slide_from_bottom', gestureDirection: 'vertical' }} />
        <Stack.Screen name="agreements/new-multi" options={{ animation: 'slide_from_bottom', gestureDirection: 'vertical' }} />
        <Stack.Screen name="offers/sent"       options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="offers/new"       options={{ animation: 'slide_from_bottom', gestureDirection: 'vertical' }} />
        <Stack.Screen name="review"           options={{ animation: 'slide_from_bottom', gestureDirection: 'vertical' }} />
        {/* Stack screens: fade_from_bottom για zoom-like αίσθηση */}
        <Stack.Screen name="settings"         options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="jury"             options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="circle"           options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="professionals"    options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="contacts-matches" options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="wallet/index"    options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="wallet/topup"    options={{ animation: 'slide_from_bottom' }} />
        <Stack.Screen name="how-it-works"         options={{ animation: 'fade_from_bottom' }} />
        <Stack.Screen name="admin/manage-admins"  options={{ animation: 'slide_from_bottom' }} />
        <Stack.Screen name="announcements/new"    options={{ animation: 'slide_from_bottom', gestureDirection: 'vertical' }} />
      </Stack>
      </WebShell>
      <FreezeBanner />
      <IncomingMessageToast />
      {!isWideWeb && <TabBar />}
      <ConsentBanner />
      <Tutorial />
      {pendingLevelUp && <LevelUpOverlay info={pendingLevelUp} onDone={() => void handleLevelUpDone()} />}
      <Level2ReviewModal visible={showLevel2Review} onClose={() => setShowLevel2Review(false)} />
      <ProfessionReminderModal visible={showProfessionReminder} onClose={() => setShowProfessionReminder(false)} />
      <AnnouncementModal visible={showAnnouncement} announcement={announcement} onClose={dismissAnnouncement} />
      <WebWatermarkOverlay />
      {!updateDismissed && !tutorialActive && (
        <UpdatePrompt status={effectiveUpdateStatus} onDismiss={() => setUpdateDismissed(true)} />
      )}
    </View>
    </TabTransitionProvider>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  loader: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
